import React, { useRef } from "react";
import PropTypes from "prop-types";
import styled, { css } from "styled-components";
import PlayButton from "./PlayButton";
import { isMobile, unwrapStyles } from "../utils";
import get from "lodash/get";
import useSize from "../hooks/useSize";
import SliderItemTag from "./SliderItemTag";
import settings from "../settings";

const SlideWrapper = styled.div`
  display: block;
  text-decoration: none;
  position: relative;
  z-index: 0;
  transition: z-index 300ms step-end;

  &: hover {
    z-index: 1;
    transition: z-index 300ms step-start;
    transition-delay: 500ms;
  }

  ${({ count, theme }) =>
    count &&
    css`
      display: flex;
    `}

  &:not(:first-child) {
    margin-top: 5px;
  }

  &.no-margin-top {
    margin-top: 0;
  }
`;

const Image = styled.img`
  aspect-ratio: ${({ theme }) => theme.image.aspectRatio};
  width: 100%;
  border-radius: ${({ theme }) => theme.image.borderRadius};
  vertical-align: top;
  ${({ theme }) => isMobile() && unwrapStyles(theme.imageMobile)}
`;

const ImageWrapper = styled.div`
  position: relative;
  overflow: hidden;
  cursor: pointer;
`;

const Label = styled.div`
  text-align: center;
  ${({ theme }) => unwrapStyles(theme.sliderItemLabel)}
  font-size: 14px;
  padding-top: 5px;
  overflow: hidden;
  text-overflow: ellipsis;
  width: 100%;
  white-space: nowrap;

  ${({ theme }) =>
    theme.widgetWidth < 1000 &&
    css`
      font-size: 12px;
    `}
`;

const Mask = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  border-radius: ${({ theme }) => theme.image.borderRadius};
  background-color: rgba(0, 0, 0, 0.65);
  opacity: 0;
  transition: opacity 0.4s;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

const ImageSlide = styled.div`
  position: relative;
  z-index: 0;
  transition: z-index 300ms step-end, transform 300ms ease;
  flex: 2;
  min-width: 0;
  line-height: 20px;

  &:hover {
    ${Mask} {
      opacity: 1;
    }
  }

  ${({ scaleOnHover }) =>
    scaleOnHover &&
    css`

      &:hover {
        transform: scale(1.1)  translate3d(${({ shiftX }) =>
          `${shiftX}, 0, 0`});
        z-index: 1;
        transition: z-index 300ms step-start, transform 300ms ease;
        transition-delay: 500ms;

      }
    }
  `}
`;

const Count = styled.div`
  flex: 1;
  img {
    height: ${({ labelHeight }) => `calc(95% - ${labelHeight}px)`};
    aspect-ratio: 1;
  }
`;

const SliderItem = React.forwardRef(
  (
    {
      item,
      className,
      imageSrc,
      isInFirstColumn,
      isInLastColumn,
      count,
      disableMagnifyOnHover,
      onGameClick,
      position,
    },
    ref,
  ) => {
    let shiftX = "0";
    if (isInFirstColumn) {
      shiftX = "4.5%";
    } else if (isInLastColumn) {
      shiftX = "-4.5%";
    }
    const labelElementRef = useRef(null);
    const size = useSize(labelElementRef);
    const labelHeight = get(size, "height");

    const { tags, magnifyOnHover, showPlayBtnOnHover } =
      settings.components.sliderItem;

    const showPlayButton = !isMobile() && showPlayBtnOnHover;

    return (
      <SlideWrapper
        data-test="slider-item"
        ref={ref}
        className={className}
        count={count}
      >
        {count && (
          <Count labelHeight={labelHeight}>
            <Image
              src={`https://casino-widgets.vaix.ai/images/number_${count}.svg`}
              alt=""
            />
          </Count>
        )}
        <ImageSlide
          data-test="image-slide"
          shiftX={shiftX}
          scaleOnHover={magnifyOnHover && !disableMagnifyOnHover}
        >
          <ImageWrapper
            onClick={(e) => {
              e.stopPropagation();
              onGameClick &&
                onGameClick({
                  gameInfo: { gameId: item.game_id, demoPlay: false, position },
                });
            }}
          >
            <Image data-test="item-image" src={imageSrc} alt="" />
            {showPlayButton && (
              <Mask data-test="item-mask">
                <PlayButton />
              </Mask>
            )}
            {tags.map(
              (tag) =>
                tag.getProps(item) && (
                  <SliderItemTag
                    component={tag.component}
                    props={tag.getProps(item)}
                    key={tag.component}
                  />
                ),
            )}
          </ImageWrapper>
          <Label data-test="item-label" ref={labelElementRef}>
            {item.game_name}
          </Label>
        </ImageSlide>
      </SlideWrapper>
    );
  },
);

SliderItem.propTypes = {
  imageSrc: PropTypes.string.isRequired,
  isInFirstColumn: PropTypes.bool,
  isInLastColumn: PropTypes.bool,
  count: PropTypes.number,
  disableMagnifyOnHover: PropTypes.bool,
  item: PropTypes.object,
};

export default SliderItem;
